import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import { t } from "i18next";
import { Link } from "react-router-dom";
import EstimateGraph from "../components/estimate/EstimateGraph";
import { useAppDispatch } from "../store/hooks";
import {
  changeTier,
  selectEstimate,
  selectEstimationResult,
} from "../store/features/estimateSlice";
import CursorIcon from "../components/Icons/CursorIcon";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useCallback, useMemo, useState } from "react";
import EstimateSlider from "../components/estimate/EstimateSlider";
import numeral from "numeral";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import MetricCard from "../components/MetricCard";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";

export default function Estimate() {
  const dispatch = useAppDispatch();

  const [sliderValue, setSliderValue] = useState(2);
  const estimate = useSelector(selectEstimate);
  const estimationResult = useSelector(selectEstimationResult);

  const sliderMarks = useMemo(() => {
    return estimationResult?.map((e) => ({
      value: e.level,
      label: numeral(e.tier).format("$0"),
    }));
  }, [estimationResult]);

  const onSliderChange = useCallback(
    (_: any, value: number | number[]) => {
      setSliderValue(Array.isArray(value) ? value[0] : value);
      const result = estimationResult?.find((e) => e.level === value);
      dispatch(changeTier(result?.tier));
    },
    [estimationResult, dispatch]
  );

  return (
    <Box>
      {estimationResult != null && estimationResult.length === 4 && (
        <Grid container spacing={1} justifyContent={"center"}>
          <Grid
            item
            sm={5}
            xs={12}
            container
            spacing={2}
            flexDirection={"row"}
            alignItems={"center"}
          >
            <Grid item sm={12}>
              <Typography variant="h6" fontWeight={"bold"} color={"secondary"}>
                {t("estimate.title")}
              </Typography>
              <Typography marginY={2} variant="subtitle2" fontWeight={"400"}>
                {t("estimate.estimateInfo")}
              </Typography>
              <Typography marginY={2} variant="subtitle2" fontWeight={"400"}>
                {t("estimate.subTitle")}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={12} alignSelf={"end"}>
              <MetricCard
                icon={
                  <VisibilityIcon
                    sx={{ width: "18px", marginRight: ".15rem" }}
                  />
                }
                label={t("estimate.potentialViews")}
                fromValue={numeral(
                  estimationResult.find((d) => d.tier === estimate.tier)
                    ?.adViews.lowerValue
                ).format("0.00a")}
                toValue={numeral(
                  estimationResult.find((d) => d.tier === estimate.tier)
                    ?.adViews.higherValue
                ).format("0.00a")}
              />
            </Grid>
            <Grid item xs={6} sm={12} alignSelf={"start"}>
              <MetricCard
                icon={
                  <CursorIcon sx={{ width: "18px", marginRight: ".15rem" }} />
                }
                label={t("estimate.potentialClicks")}
                fromValue={numeral(
                  estimationResult.find((d) => d.tier === estimate.tier)
                    ?.adClicks.lowerValue
                ).format("0.[00]a")}
                toValue={numeral(
                  estimationResult.find((d) => d.tier === estimate.tier)
                    ?.adClicks.higherValue
                ).format("0.[00]a")}
              />
            </Grid>
            {estimate.email != null && estimate.email.trim() !== "" && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" fontWeight={"400"}>
                  {t("estimate.emailSentBefore") + " "}
                  <a href={"mailto:" + estimate.email}>{estimate.email}</a>
                  {" " + t("estimate.emailSentAfter")}
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid item sm={6} xs={12} container spacing={2}>
            <Grid item xs={12}>
              <Container>
                <EstimateSlider
                  value={sliderValue}
                  step={1}
                  onChange={onSliderChange}
                  marks={sliderMarks}
                  min={1}
                  max={4}
                  valueLabelDisplay="off"
                  sx={{ marginTop: "1rem" }}
                />
              </Container>
              <EstimateGraph level={sliderValue} />
            </Grid>
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={12}>
                <Button
                  to="/preview"
                  component={Link}
                  variant="contained"
                  fullWidth
                  size="large"
                  className="rounded-button"
                >
                  <Typography fontWeight={"bold"} marginRight={1}>
                    {t("estimate.seeAdsAction")}
                  </Typography>
                  <ArrowForwardIcon />
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  to="/callback?backToEstimate=true"
                  component={Link}
                  variant="outlined"
                  fullWidth
                  className="rounded-button"
                >
                  <Typography fontWeight={"bold"} marginRight={1}>
                    {t("estimate.bookAppointmentAction")}
                  </Typography>
                  <ArrowForwardIcon />
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  to="/"
                  component={Link}
                  variant="outlined"
                  className="rounded-button"
                  fullWidth
                >
                  <Typography fontWeight={"bold"} marginRight={1}>
                    {t("estimate.refreshEstimateAction")}
                  </Typography>
                  <ArrowForwardIcon />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
